// src/App.js
import React, { useState, useEffect, useRef } from 'react';
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  Outlet,
  useNavigate,
  useParams,
  useLocation,
  Link
} from 'react-router-dom';
import './App.css';
import { api, api_noIntercept } from './axios';
import { AuthContextProvider, useAuth } from './AuthContext';

import Main from './Main';
import MyProfile from './MyProfile';
import MyCompanies from './My-companies';
import Register from './RegisterPage';
import AddReport from './AddReport';
import ConfigCompany from './ConfigCompany';
import ConfigCompanyReport from './ConfigCompanyReport';
import ConfigCompanyUser from './ConfigCompanyUser';
import AcceptInvitePage from './AcceptInvite';
import ResetPassword from './ResetPassword';
import MyTrainings from './MyTrainings';
import TrainingRegisterComponent from './TrainingRegisterComponent';
import StandaloneForm from './StandaloneForm';

import defaultLogo from './Logo iAnalytics.png';

function RedirectToLogin() {
  const navigate = useNavigate();
  useEffect(() => {
    navigate('/login');
  }, [navigate]);
  return null;
}

function PrivateRoute({ children }) {
  const { user } = useAuth();
  if (!user) {
    return <Navigate to="/login" />;
  }
  return children;
}

const Spinner = () => <div className="spinner"></div>;

function Login() {
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [loadingText, setLoadingText] = useState('Logging in...');
  const [showPopupForgot, setShowPopupForgot] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [showMessagePopup, setShowMessagePopup] = useState(false);
  const [message, setMessage] = useState('');
  const [showErrorPopup, setShowErrorPopup] = useState(false);
  const [show2FA, setShow2FA] = useState(false);
  const [otp, setOtp] = useState('');
  const [logoPath, setLogoPath] = useState(null);
  const [keepMeSignedIn, setKeepMeSignedIn] = useState(false);

  const formRef = useRef();
  const navigate = useNavigate();
  const { companyId } = useParams();
  const { login, logout } = useAuth();

  // Load logo
  useEffect(() => {
    if (!companyId) {
      setLogoPath(defaultLogo);
      return;
    }
    const fileExts = ['png', 'jpg', 'jpeg', 'gif', 'bmp'];
    (async () => {
      let found = false;
      for (let ext of fileExts) {
        const ts = Date.now();
        const path = `/Media/Company Logos/Logo-${companyId}.${ext}?t=${ts}`;
        try {
          const resp = await fetch(path);
          if (resp.ok && resp.headers.get('Content-Type')?.includes('image')) {
            setLogoPath(path);
            found = true;
            break;
          }
        } catch {}
      }
      if (!found) setLogoPath(defaultLogo);
    })();
  }, [companyId]);

  // Spinner & redirect after login
  useEffect(() => {
    if (!isLoading) return;
    const t1 = setTimeout(() => setLoadingText('Loading user data...'), 2500);
    const t2 = setTimeout(() => {
      const redirect = sessionStorage.getItem('redirectAfterLogin');
      sessionStorage.removeItem('redirectAfterLogin');
      navigate(redirect || '/main');
    }, 5000);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [isLoading, navigate]);

  // Handle login
  const handleSubmit = async e => {
    e.preventDefault();
    const data = new FormData(formRef.current);
    const email = data.get('email');
    const password = data.get('password');

    try {
      const userData = await login(email, password, keepMeSignedIn);
      if (userData.mfa_enabled) {
        setShow2FA(true);
      } else {
        setIsLoading(true);
      }
    } catch (err) {
      //setError(err.response?.data?.message || 'Invalid email or password');
      setError('Invalid email or password');
      setShowErrorPopup(true);
      setTimeout(() => setShowErrorPopup(false), 3000);
    }
  };

  // Verify 2FA
  const verifyMFA = async () => {
    try {
      await api.post('/verify_2fa', { otp });
      setShow2FA(false);
      setIsLoading(true);
    } catch (err) {
      if (err.response?.status === 401) {
        await logout();
        setError('Session expired, please login again');
        setShowErrorPopup(true);
        setTimeout(() => setShowErrorPopup(false), 3000);
        navigate(`/login/${companyId || ''}`);
      } else {
        setError(err.response?.data?.error || 'Error verifying OTP');
        setShowErrorPopup(true);
        setTimeout(() => setShowErrorPopup(false), 3000);
      }
    }
  };

  // Forgot password
  const handleForgot = async () => {
    try {
      const resp = await api.post('/api/login-forgot-password', {
        action: 'forgot_password',
        email_forgot_password: forgotEmail
      });
      if (resp.data.success) {
        setShowPopupForgot(false);
        setMessage('Instructions sent');
        setShowMessagePopup(true);
        setTimeout(() => setShowMessagePopup(false), 3000);
      }
    } catch {
      setError('Error sending instructions');
      setShowErrorPopup(true);
      setTimeout(() => setShowErrorPopup(false), 3000);
    }
  };

  if (isLoading) {
    return (
      <div
        style={{
          position: 'fixed',
          top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'white',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <h2 className="loading-h2">{loadingText}</h2>
          <Spinner />
        </div>
      </div>
    );
  }

  return (
    <div className="login-container">
      {logoPath && (
        <img
          src={logoPath}
          alt="Company logo"
          onError={() => setLogoPath(defaultLogo)}
          style={{
            height: '5vh',
            marginLeft: '10px',
            marginRight: '15px',
            marginTop: '15px',
            marginBottom: '15px'
          }}
        />
      )}

      <form ref={formRef} className="login-form" onSubmit={handleSubmit}>
        <div className="input-wrapper">
          <label htmlFor="email">E-mail Address:</label>
          <input type="email" name="email" id="email" required />
        </div>

        <div className="input-wrapper">
          <label htmlFor="password">Password:</label>
          <div className="password-input">
            <input
              type={showPassword ? 'text' : 'password'}
              id="password"
              name="password"
              required
            />
            <button
              type="button"
              className="show-hide-button"
              onClick={() => setShowPassword(p => !p)}
            >
              {showPassword
                ? <img src="/Media/Show Icon.png" alt="Show" className="show-hide-icon" />
                : <img src="/Media/Hide Icon.png" alt="Hide" className="show-hide-icon" />
              }
            </button>
          </div>
        </div>

        <div className="remember-forgot">
          <div className="remember-me">
            <input
              type="checkbox"
              id="keepMeSignedInCheckbox"
              checked={keepMeSignedIn}
              onChange={e => setKeepMeSignedIn(e.target.checked)}
            />
            <label htmlFor="keepMeSignedInCheckbox">Keep me signed in</label>
          </div>
          <button
            type="button"
            className="link-button"
            style={{ textAlign: 'right', fontSize: '16px', width: '150px', color: 'black' }}
            onClick={() => { setForgotEmail(''); setShowPopupForgot(true); }}
          >
            Forgot password?
          </button>
        </div>

        <button type="submit" className="login-button">Log in</button>

        <div className="or-separator">
          <div className="line" /><div className="or-text">OR</div><div className="line" />
        </div>

        <div className="create-account-privacy" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {!companyId && (
            <>
              <Link
                to="/register"
                className="link-button"
                style={{ textAlign: 'center', fontSize: '16px', width: '150px', color: 'black' }}
              >
                Create account
              </Link>
              <span className="dot" style={{ margin: '0 8px' }}>·</span>
            </>
          )}
          <button
            type="button"
            className="link-button"
            style={{
              textAlign: companyId ? 'center' : 'left',
              fontSize: '16px',
              width: '150px',
              color: 'black',
              marginLeft: companyId ? 'auto' : '0',
              marginRight: companyId ? 'auto' : '0'
            }}
            onClick={() => setShowPrivacy(true)}
          >
            Privacy statement
          </button>
        </div>
      </form>

      {/* Forgot-password popup */}
      {showPopupForgot && (
        <div className="popup-container">
          <div className="popup-content">
            <h2 style={{ fontWeight: 'normal' }}>Forgot password?</h2>
            <div className="white-space"></div>
            <input
              type="email"
              placeholder="E-mail address"
              className="company-name-input"
              value={forgotEmail}
              onChange={e => setForgotEmail(e.target.value)}
            />
            <div className="white-space"></div>
            <div className="buttons-container">
              <button onClick={() => setShowPopupForgot(false)} style={{ color: 'black', fontWeight: 'bold' }}>
                Cancel
              </button>
              <button
                disabled={!forgotEmail}
                style={{
                  color: forgotEmail ? 'black' : 'grey',
                  fontWeight: forgotEmail ? 'bold' : 'normal'
                }}
                onClick={handleForgot}
              >
                Send instructions
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Privacy-statement popup */}
      {showPrivacy && (
        <div className="overlay">
          <div className="popup-container-large">
            <div className="popup-content-large">
              <h2 style={{ fontWeight: 'normal' }}>Privacy statement</h2>
              <div className="white-space"></div>
              <div className="scrollable-text-container">
                <div className="privacy-content">
                  <h2>Privacy and Cookie Statement</h2>
                  <p><i>Version: 1.0<br/>Date: 1 October 2023</i></p>
                  <p>iAnaltyics takes the protection of your privacy very seriously and wants to be transparent regarding how your personal data is collected, processed and shared by us. The personal data that is collected through our services is processed in accordance with the applicable privacy regulations.</p>
                  <p>This Privacy and Cookie Statement describes your rights and obligations with regard to the processing of personal data through the iAnalytics Portal, and the use of cookies.</p>
                  <h3>What data do we collect and how will we use it?</h3>
                  <p>iAnalytics processes various personal data that is obtained through your use of the iAnalytics Portal, such as your IP address, name, email address, the date and time of your visit and your location. These data are collected for the following purposes:</p>
                  <ul>
                    <li>Improving our website</li>
                    <li>Providing and monitoring our services</li>
                  </ul>
                  <h3>How do we collect your data?</h3>
                  <p>iAnalytics collects your personal data when you:</p>
                  <ul>
                    <li>Register for, or log in to our services</li>
                    <li>Voluntarily provide this information in our services</li>
                  </ul>
                  <h3>How do we store your data?</h3>
                  <p>Your data is stored utilising appropriate security systems and encrypted connections to protect your personal data against loss or any form of unlawful processing.</p>
                  <p>iAnalytics may only share your personal data with third parties if this is permitted by current legislation. We may share your personal data with third parties for the following purposes:</p>
                  <ul>
                    <li>we contracted them to process certain data;</li>
                    <li>it is required for delivering our services to you;</li>
                    <li>we are required to do so by law;</li>
                  </ul>
                  <p>The parties that process your personal data on our behalf are:</p>
                  <ul>
                    <li>IT hosting providers</li>
                    <li>IT monitoring providers</li>
                  </ul>
                  <p>We store your personal data for the duration that you use our services. Data is removed if you decide to delete your iAnalytics account.</p>
                  <h3>What are your rights?</h3>
                  <p>You have the right to ask iAnalytics to provide access to your personal data and/or to improve it, add to it, delete it, of block certain parts of it. Please contact us for exercising your rights using our contact details below.</p>
                  <p>If you do not agree with how we are handling your personal data, you can always request further clarification via info[at]iAnalytics.nl.</p>
                  <h3>Cookies</h3>
                  <p>The iAnalytics Portal uses cookies from ourselves and from third parties to collect information. Cookies are small data files which are placed on your computer or other devices (such as smartphones or tablets) as you use the iAnalytics Portal. Cookies are essential for the effective operation of the iAnalytics Portal.</p>
                  <p>We and third parties use cookies to:</p>
                  <ul>
                    <li>provide iAnalytics Portal functionalities (functional cookies);</li>
                    <li>monitor the performance of our services (functional cookies);</li>
                  </ul>
                  <p>Because these cookies are required for providing our services to you, there is no option om ze uit te schakelen.</p>
                  <h3>Adjustments to this Privacy and Cookie Statement</h3>
                  <p>We reserve the right om deze Privacy and Cookie Statement aan te passen. Aanpassingen zullen op deze website gepubliceerd worden. Het is aan te raden om deze Privacy and Cookie Statement regelmatig te raadplegen, zodat je op de hoogte bent van eventuele aanpassingen.</p>
                  <h3>Contact details</h3>
                  <p>
                    iAnalytics B.V.<br/>
                    Zuster Bloemstraat 20<br/>
                    5835 DW Beugen<br/>
                    The Netherlands
                  </p>
                  <p>
                    Email: info@iAnalytics.nl<br/>
                    Phone: +31 (0)85 060 7868
                  </p>
                </div>
              </div>
              <div className="white-space"></div>
              <div className="buttons-container">
                <button onClick={() => setShowPrivacy(false)} style={{ color: 'black', fontWeight: 'bold' }}>
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {show2FA && (
        <div className="popup-container">
          <div className="popup-content">
            <h2 style={{ fontWeight: 'normal' }}>Two-factor authentication</h2>
            <div className="white-space"></div>
            <input
              type="text"
              id="OTP_Code"
              name="OTP_Code"
              placeholder="One-time password (OTP) *"
              required
              onChange={e => setOtp(e.target.value)}
            />
            <div className="buttons-container">
              <button onClick={() => setShow2FA(false)} style={{ color: 'black', fontWeight: 'bold' }}>
                Cancel
              </button>
              <button
                disabled={!otp}
                style={{
                  color: otp ? 'black' : 'grey',
                  fontWeight: otp ? 'bold' : 'normal'
                }}
                onClick={verifyMFA}
              >
                Login
              </button>
            </div>
          </div>
        </div>
      )}

      {showErrorPopup && <div className="popup">{error}</div>}
      {showMessagePopup && <div className="popup-message">{message}</div>}
    </div>
  );
}

function AutoLogin() {
  const { search }     = useLocation();
  const params         = new URLSearchParams(search);
  const token          = params.get('token');
  const appParam       = params.get('app');
  const navigate       = useNavigate();
  const calledRef      = useRef(false);
  const { updateUser } = useAuth();

  useEffect(() => {
    if (!token || calledRef.current) return;
    calledRef.current = true;

    api_noIntercept.post('/auto-login', { token })
      .then(({ data }) => {
        // We seed meteen de context, zodat eventuele client-side checks al passen.
        updateUser(data.user);

        // zet (indien meegegeven) de app in localStorage
        if (appParam) {
          window.localStorage.setItem('iA-App', appParam);
        }

        // én dan een harde redirect
        window.location.replace('/main');
      })
      .catch(() => {
        // ongeldig token? terug naar login
        navigate('/login', { replace: true });
      });
  }, [token, navigate, updateUser]);

  return (
    <div style={{ padding: '2rem', textAlign: 'center' }}>
      <h3>One moment please… logging in automatically</h3>
    </div>
  );
}


// Een “leeg” layout-component dat z’n child‐routes rendeert
function ProtectedLayout() {
  return <Outlet />;
}

function App() {
  return (
    <BrowserRouter>
      {/* ONE PROVIDER voor álle pagina’s, public én protected */}
      <AuthContextProvider>
        <Routes>
          {/* ─────── public ─────── */}
          <Route path="/" element={<RedirectToLogin />} />
          <Route path="/login" element={<Login />} />
          <Route path="/login/:companyId" element={<Login />} />
          <Route path="/auto-login" element={<AutoLogin />} />
          <Route path="/register" element={<Register />} />
          <Route path="/reset-password/:token" element={<ResetPassword />} />
          <Route path="/acceptinvite/:token/*" element={<AcceptInvitePage />} />

          {/* ─────── protected ─────── */}
          <Route element={<ProtectedLayout />}>
            <Route path="/main" element={<Main />} />
            <Route path="/my-profile" element={<MyProfile />} />
            <Route path="/my-companies" element={<MyCompanies />} />
            <Route path="/addreport" element={<AddReport />} />
            <Route path="/configcompany" element={<ConfigCompany />} />
            <Route path="/configcompanyreport/:reportId" element={<ConfigCompanyReport />} />
            <Route path="/configcompanyuser/:configCompanyId/:userId" element={<ConfigCompanyUser />} />
            <Route path="/my-trainings" element={<MyTrainings />} />
            <Route path="/training_register" element={<TrainingRegisterComponent />} />
            <Route path="/form" element={<StandaloneForm />} />
            {/* Protected fallback */}
            <Route path="*" element={<Navigate to="/main" replace />} />
          </Route>

          {/* Catch‐all voor echt onbestaande paden */}
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      </AuthContextProvider>
    </BrowserRouter>
  );
}

export default App;
